import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  CONTACT_POINT__FEATURE_KEY,
  ContactPointState,
} from './contact-point.reducer';

/**
 * Retrieves the ContactPointState from the feature state.
 *
 * @returns The ContactPointState from the feature state.
 */
export const getContactPointState = createFeatureSelector<ContactPointState>(
  CONTACT_POINT__FEATURE_KEY,
);

/**
 * Selects the attribute from the contact point state.
 *
 * @param state - The contact point state.
 * @returns The attribute.
 */
export const selectAttr = createSelector(
  getContactPointState,
  (state: ContactPointState) => state.attr,
);

/**
 * Selects the attribute value from the contact point state.
 *
 * @param state - The contact point state.
 * @returns The attribute value.
 */
export const selectAttrValue = createSelector(
  getContactPointState,
  (state: ContactPointState) => state.value,
);

/**
 * Selects the form errors from the contact point state.
 *
 * @param state - The contact point state.
 * @returns The form errors object.
 */
export const selectFormErrors = createSelector(
  getContactPointState,
  (state: ContactPointState) => state.formErrors || {},
);

/**
 * Selects the 'adding' property from the contact point state.
 * Returns true if 'adding' is truthy, otherwise returns false.
 *
 * @param state - The contact point state.
 * @returns The value of 'adding' property.
 */
export const selectContactPointProcessing = createSelector(
  getContactPointState,
  (state: ContactPointState) => state.processing || false,
);

/**
 * Selects the resending state of the contact point from the contact point state.
 * If the resending state is not available, it defaults to false.
 *
 * @param state - The contact point state.
 * @returns The resending state of the contact point.
 */
export const selectContactPointResending = createSelector(
  getContactPointState,
  (state: ContactPointState) => state.resending || false,
);

/**
 * Selects the sended state of the contact point from the contact point state.
 * If the sended state is not available, it defaults to false.
 *
 * @param state - The contact point state.
 * @returns The sended state of the contact point.
 */
export const selectContactPointSended = createSelector(
  getContactPointState,
  (state: ContactPointState) => state.sended || false,
);

export const selectCountdown = createSelector(
  getContactPointState,
  (state: ContactPointState) => ({
    times: state.sendVerificationCodeCount,
    end: state.countdown,
  }),
);

export const selectContactPoint = createSelector(
  selectAttr,
  selectAttrValue,
  (attr, value) => ({ attr, value }),
);

export const ContactPointSelector = {
  selectAttr,
  selectAttrValue,
  selectContactPoint,
  selectFormErrors,
  selectContactPointProcessing,
  selectContactPointResending,
  selectContactPointSended,
  selectCountdown,
};
